import { useMemo } from 'react';
import { Gesture } from 'react-native-gesture-handler';
import Animated, {
  useSharedValue,
  withSpring,
  withSequence,
  withTiming,
  runOnJS,
} from 'react-native-reanimated';
import type { SharedValue } from 'react-native-reanimated';

const TOUCH_SPRING = { stiffness: 520, damping: 38, mass: 0.45 };
const MY_SPRING = { stiffness: 620, damping: 36, mass: 0.45 };

export interface UseJellyGestureOptions {
  /** Width of the gesture hit area (e.g. image + content) for jelly center math. */
  fullWidthRef: SharedValue<number>;
  /** Content width passed to onEnd (e.g. for direction calculation). */
  contentWidthRef: SharedValue<number>;
  height: number;
  onEnd: (touchX: number, contentWidth: number) => void;
  /** Optional: called when touch starts (e.g. for jelly haptic). */
  onJellyStart?: () => void;
  /** Optional: when provided, card tap/pan wait for scroll to fail (list scroll priority). */
  scrollGestureRef?: React.RefObject<unknown>;
  /**
   * When `false`, only the tap jelly runs (no pan follower). Use inside `FlatList` rows: pan `onChange`
   * fires many `withSpring` updates per frame and causes scroll jank. Default `true` for standalone cards.
   */
  enablePanJelly?: boolean;
  /**
   * When `true`, tap still fires `onEnd` but skips skew/scale jelly (use in `FlatList` rows).
   */
  lightTapFeedback?: boolean;
  /**
   * When set, tap/pan update these (local `e.x` / `e.y`) for list-row 3D tilt. Use with `useDualLayerTiltFromTouch`
   * in `aware-card` — no extra `Gesture.Pan` so the list scroll is not blocked.
   */
  awareTouchX?: SharedValue<number>;
  awareTouchY?: SharedValue<number>;
}

export interface JellySharedValues {
  rotateX: SharedValue<number>;
  rotateY: SharedValue<number>;
  jellySkewX: SharedValue<number>;
  jellySkewY: SharedValue<number>;
  jellyScaleX: SharedValue<number>;
  jellyScaleY: SharedValue<number>;
  centerPXCard: SharedValue<number>;
  centerPYCard: SharedValue<number>;
}

export function useJellyGesture(options: UseJellyGestureOptions): {
  gesture: ReturnType<typeof Gesture.Simultaneous> | ReturnType<typeof Gesture.Tap>;
  jellySharedValues: JellySharedValues;
} {
  const {
    fullWidthRef,
    contentWidthRef,
    height,
    onEnd,
    onJellyStart,
    scrollGestureRef,
    enablePanJelly = true,
    lightTapFeedback = false,
    awareTouchX,
    awareTouchY,
  } = options;

  const rotateX = useSharedValue(0);
  const rotateY = useSharedValue(0);
  const jellySkewX = useSharedValue(0);
  const jellySkewY = useSharedValue(0);
  const jellyScaleX = useSharedValue(1);
  const jellyScaleY = useSharedValue(1);
  const centerPXCard = useSharedValue(0);
  const centerPYCard = useSharedValue(0);

  const jellySharedValues: JellySharedValues = useMemo(
    () => ({
      rotateX,
      rotateY,
      jellySkewX,
      jellySkewY,
      jellyScaleX,
      jellyScaleY,
      centerPXCard,
      centerPYCard,
    }),
    [],
  );

  const pan = useMemo(() => {
    if (!enablePanJelly) {
      return null;
    }
    let p = Gesture.Pan()
      .minDistance(15)
      .activeOffsetY([-12, 12])
      .failOffsetY([-8, 8])
      .failOffsetX([-100, 100])
      .onBegin((e) => {
        'worklet';
        if (onJellyStart) {
          runOnJS(onJellyStart)();
        }
        if (awareTouchX != null && awareTouchY != null) {
          awareTouchX.value = e.x;
          awareTouchY.value = e.y;
        }
        const width = fullWidthRef.value;
        const centerPX = Math.ceil(Math.min(Math.max((e.x / width) * 100, 0), 100));
        const centerPY = Math.ceil(Math.min(Math.max((e.y / height) * 100, 0), 100));
        centerPXCard.value = withSpring(centerPX, TOUCH_SPRING);
        centerPYCard.value = withSpring(centerPY, TOUCH_SPRING);
      })
      .onChange((e) => {
        'worklet';
        const width = fullWidthRef.value;
        if (awareTouchX != null && awareTouchY != null) {
          awareTouchX.value = e.x;
          awareTouchY.value = e.y;
        }
        const centerX = width / 2;
        const centerY = height / 2;
        const offX = (e.x - centerX) / centerX;
        const offY = (e.y - centerY) / centerY;
        const centerPX = Math.ceil(Math.min(Math.max((e.x / width) * 100, 0), 100));
        const centerPY = Math.ceil(Math.min(Math.max((e.y / height) * 100, 0), 100));
        centerPXCard.value = withSpring(centerPX, MY_SPRING);
        centerPYCard.value = withSpring(centerPY, MY_SPRING);
        rotateY.value = withSpring(Math.min(Math.max(-offY * 0.1, -0.06), 0.06), MY_SPRING);
        rotateX.value = withSpring(Math.min(Math.max(-offX * 0.1, -0.06), 0.06), MY_SPRING);
        jellySkewX.value = withSpring(Math.min(Math.max(offX * 0.008, -0.06), 0.06), MY_SPRING);
        jellySkewY.value = withSpring(Math.min(Math.max(offY * 0.008, -0.06), 0.06), MY_SPRING);
        jellyScaleX.value = withSpring(Math.min(Math.max(1 - Math.abs(offX) * 0.02, 0.985), 1), MY_SPRING);
        jellyScaleY.value = withSpring(Math.min(Math.max(1 - Math.abs(offY) * 0.02, 0.985), 1), MY_SPRING);
      })
      .onEnd((e) => {
        'worklet';
        runOnJS(onEnd)(e.x, contentWidthRef.value);
      })
      .onFinalize(() => {
        'worklet';
        jellyScaleX.value = withSpring(1, MY_SPRING);
        jellyScaleY.value = withSpring(1, MY_SPRING);
        rotateY.value = withSpring(0, MY_SPRING);
        rotateX.value = withSpring(0, MY_SPRING);
        jellySkewX.value = withSpring(0, MY_SPRING);
        jellySkewY.value = withSpring(0, MY_SPRING);
        centerPXCard.value = withSpring(50, MY_SPRING);
        centerPYCard.value = withSpring(50, MY_SPRING);
        if (awareTouchX != null && awareTouchY != null) {
          const wv = fullWidthRef.value;
          if (wv > 0) {
            awareTouchX.value = wv * 0.5;
          }
          awareTouchY.value = height * 0.5;
        }
      });
    if (scrollGestureRef != null) {
      try {
        p = p.requireExternalGestureToFail(scrollGestureRef as any);
      } catch (_) {
        // Ref type mismatch on some RN versions; activeOffsetY / failOffsetY still bias toward scroll
      }
    }
    return p;
  }, [height, onEnd, onJellyStart, fullWidthRef, contentWidthRef, scrollGestureRef, enablePanJelly, awareTouchX, awareTouchY]);

  const tap = useMemo(() => {
    let t = Gesture.Tap()
      // Tight slop: large values let vertical scrolls count as taps on release (unwanted haptics).
      .maxDistance(12)
      .maxDuration(2000)
      .onStart((e) => {
        'worklet';
        if (onJellyStart) {runOnJS(onJellyStart)();}
        if (awareTouchX != null && awareTouchY != null) {
          awareTouchX.value = e.x;
          awareTouchY.value = e.y;
        }
        if (lightTapFeedback) {
          return;
        }
        const width = fullWidthRef.value;
        const centerX = width / 2;
        const centerY = height / 2;
        const offX = (e.x - centerX) / centerX;
        const offY = (e.y - centerY) / centerY;
        rotateX.value = withSequence(
          withTiming(offY * 0.05, { duration: 40 }),
          withSpring(0, MY_SPRING),
        );
        rotateY.value = withSequence(
          withTiming(offX * 0.05, { duration: 40 }),
          withSpring(0, MY_SPRING),
        );
        jellySkewX.value = withSequence(
          withTiming(offX * 0.004, { duration: 40 }),
          withSpring(0, MY_SPRING),
        );
        jellySkewY.value = withSequence(
          withTiming(offY * 0.004, { duration: 40 }),
          withSpring(0, MY_SPRING),
        );
        jellyScaleX.value = withSequence(
          withTiming(1 - Math.abs(offX) * 0.012, { duration: 40 }),
          withSpring(1, MY_SPRING),
        );
        jellyScaleY.value = withSequence(
          withTiming(1 - Math.abs(offY) * 0.012, { duration: 40 }),
          withSpring(1, MY_SPRING),
        );
      })
      .onEnd((e) => {
        'worklet';
        runOnJS(onEnd)(e.x, contentWidthRef.value);
      })
      .onFinalize(() => {
        'worklet';
        if (awareTouchX == null || awareTouchY == null) {
          return;
        }
        const wv = fullWidthRef.value;
        if (wv > 0) {
          awareTouchX.value = wv * 0.5;
        }
        awareTouchY.value = height * 0.5;
      });
    if (scrollGestureRef != null) {
      try {
        t = t.requireExternalGestureToFail(scrollGestureRef as any);
      } catch (_) {}
    }
    return t;
  }, [height, onEnd, onJellyStart, fullWidthRef, contentWidthRef, scrollGestureRef, lightTapFeedback, awareTouchX, awareTouchY]);

  const gesture = useMemo(() => {
    if (pan != null) {
      return Gesture.Simultaneous(tap, pan);
    }
    return tap;
  }, [tap, pan]);

  return { gesture, jellySharedValues };
}
